/** Category form types resolved from rental category slug/name. */
export const RENTAL_FORM_TYPES = {
    VEHICLE: "vehicle",
    CLOTHING: "clothing",
    GADGETS: "gadgets",
    CAMERA: "camera",
    PARTY: "party",
    FURNITURE: "furniture",
    SPORTS: "sports",
    OUTDOOR: "outdoor",
    ELECTRONICS: "electronics",
    LUXURY: "luxury",
};

const MATCH_RULES = [
    { type: RENTAL_FORM_TYPES.VEHICLE, tokens: ["vehicle", "vehicles", "car", "cars", "bike", "bikes", "automobile", "auto", "scooter", "van", "suv", "motorcycle"] },
    { type: RENTAL_FORM_TYPES.CLOTHING, tokens: ["cloth", "clothing", "apparel", "fashion", "wear", "dress", "costume", "outfit"] },
    { type: RENTAL_FORM_TYPES.CAMERA, tokens: ["camera", "lens", "photography", "drone", "camcorder"] },
    { type: RENTAL_FORM_TYPES.GADGETS, tokens: ["gadget", "laptop", "tablet", "phone", "mobile", "computer", "macbook", "ipad"] },
    { type: RENTAL_FORM_TYPES.ELECTRONICS, tokens: ["electronic", "electronics", "tv", "television", "speaker", "projector", "console"] },
    {
        type: RENTAL_FORM_TYPES.OUTDOOR,
        tokens: [
            "trekking",
            "trek",
            "camping",
            "outdoor-gear",
            "outdoor",
            "hiking",
            "hike",
            "backpack",
            "sleeping-bag",
            "sleeping bag",
            "trekking-pole",
            "trekking-poles",
            "trekking pole",
        ],
    },
    { type: RENTAL_FORM_TYPES.PARTY, tokens: ["party", "event-equipment", "marquee", "sound-system", "lighting", "event-tent"] },
    { type: RENTAL_FORM_TYPES.FURNITURE, tokens: ["furniture", "sofa", "chair", "table", "bed", "desk"] },
    { type: RENTAL_FORM_TYPES.SPORTS, tokens: ["sport", "sports", "fitness", "gym"] },
    { type: RENTAL_FORM_TYPES.LUXURY, tokens: ["luxury", "premium", "designer", "watch", "jewelry", "jewellery"] },
];

const FUEL_TYPE_OPTIONS = ["Petrol", "Diesel", "CNG", "Electric", "Hybrid", "Other"];
const TRANSMISSION_OPTIONS = ["Manual", "Automatic", "AMT", "CVT", "DCT", "Other"];
const CONDITION_OPTIONS = ["New", "Like New", "Good", "Fair"];
const GENDER_OPTIONS = ["Men", "Women", "Unisex", "Kids"];
const OUTDOOR_GEAR_TYPE_OPTIONS = [
    "Tent",
    "Backpack",
    "Sleeping bag",
    "Trekking poles",
    "Camping stove",
    "Headlamp / torch",
    "Other",
];
const SEASON_RATING_OPTIONS = ["Summer", "3-season", "4-season", "Winter", "All-season"];

/** Select options for category management UI */
export const RENTAL_FORM_TYPE_OPTIONS = [
    { value: RENTAL_FORM_TYPES.VEHICLE, label: "Vehicle" },
    { value: RENTAL_FORM_TYPES.CLOTHING, label: "Clothing & apparel" },
    { value: RENTAL_FORM_TYPES.GADGETS, label: "Gadgets" },
    { value: RENTAL_FORM_TYPES.CAMERA, label: "Camera & photography" },
    { value: RENTAL_FORM_TYPES.PARTY, label: "Party & event equipment" },
    { value: RENTAL_FORM_TYPES.FURNITURE, label: "Furniture" },
    { value: RENTAL_FORM_TYPES.SPORTS, label: "Sports & outdoor" },
    { value: RENTAL_FORM_TYPES.OUTDOOR, label: "Camping / trekking / outdoor gear" },
    { value: RENTAL_FORM_TYPES.ELECTRONICS, label: "Electronics" },
    { value: RENTAL_FORM_TYPES.LUXURY, label: "Luxury items" },
];

const FORM_TYPE_VALUES = new Set(Object.values(RENTAL_FORM_TYPES));

/**
 * @param {{ form_type?: string, slug?: string, name?: string } | null | undefined} category
 * @returns {string | null}
 */
export const resolveCategoryFormType = (category) => {
    if (!category) return null;

    const fromDb = category.form_type ? String(category.form_type).toLowerCase() : "";
    if (fromDb && FORM_TYPE_VALUES.has(fromDb)) {
        return fromDb;
    }

    const haystack = `${category.slug || ""} ${category.name || ""}`.toLowerCase();
    for (const rule of MATCH_RULES) {
        if (rule.tokens.some((token) => haystack.includes(token))) {
            return rule.type;
        }
    }
    return null;
};

export const isVehicleFormType = (formType) => formType === RENTAL_FORM_TYPES.VEHICLE;

/** How rental rates are collected in the pricing step. */
export const RENTAL_PRICING_BASIS = {
    HOUR: "hour",
    DAY: "day",
    HYBRID: "hybrid",
};

const DAY_PRICING_FORM_TYPES = new Set([
    RENTAL_FORM_TYPES.OUTDOOR,
    RENTAL_FORM_TYPES.CLOTHING,
    RENTAL_FORM_TYPES.FURNITURE,
    RENTAL_FORM_TYPES.PARTY,
    RENTAL_FORM_TYPES.SPORTS,
]);

/**
 * @param {{ form_type?: string, slug?: string, name?: string } | null | undefined} category
 * @returns {"hour" | "day" | "hybrid"}
 */
export const getCategoryPricingBasis = () => RENTAL_PRICING_BASIS.HYBRID;

/** @returns {boolean} */
export const isRentalPricingFieldVisible = (basis, field) => {
    if (field === "price_per_km") {
        return false;
    }
    return field === "price_per_hour" || field === "price_per_day";
};

/** @returns {boolean} */
export const isRentalPricingFieldRequired = (basis, field) => {
    return field === "price_per_hour" || field === "price_per_day";
};

/** @returns {boolean} */
export const isRentalPricingComplete = (data, basis) => {
    if (!data) return false;
    const hourly = Number(data.price_per_hour);
    const daily = Number(data.price_per_day);
    const hasHour = Number.isFinite(hourly) && hourly > 0;
    const hasDay = Number.isFinite(daily) && daily > 0;
    return hasHour && hasDay;
};

/**
 * @param {Record<string, unknown>} data
 */
export const normalizeRentalPricingPayload = (data, basis) => {
    return { ...data, price_per_km: null };
};

/**
 * Client-side pricing validation (discount/admin/advance rules unchanged).
 * @returns {Record<string, string>}
 */
export const validateRentalPricing = (data, basis) => {
    const errors = {};
    const hourly = Number(data.price_per_hour);
    const daily = Number(data.price_per_day);
    const hasHour = Number.isFinite(hourly) && hourly > 0;
    const hasDay = Number.isFinite(daily) && daily > 0;

    if (!hasHour) errors.price_per_hour = "Enter price per hour.";
    if (!hasDay) errors.price_per_day = "Enter price per day.";

    const discountOn = data.discount_type === "flat" || data.discount_type === "percent";
    if (discountOn && (data.discount_value === "" || data.discount_value == null)) {
        errors.discount_value = "Enter the discount amount or percentage.";
    }
    if (data.discount_type === "percent" && data.discount_value !== "" && Number(data.discount_value) > 100) {
        errors.discount_value = "Percentage discount cannot exceed 100.";
    }

    const adminChargeOn = data.admin_charge_type === "flat" || data.admin_charge_type === "percent";
    if (!adminChargeOn) {
        errors.admin_charge_value = "Admin charge is required.";
    } else if (data.admin_charge_value === "" || data.admin_charge_value == null) {
        errors.admin_charge_value = "Enter the admin charge amount or percentage.";
    } else if (data.admin_charge_type === "flat" && Number(data.admin_charge_value) <= 0) {
        errors.admin_charge_value = "Enter a valid admin charge amount.";
    }

    const advanceOn = data.advance_type === "flat" || data.advance_type === "percent";
    if (advanceOn && (data.advance_value === "" || data.advance_value == null)) {
        errors.advance_value = "Enter the advance amount or percentage.";
    }
    if (data.advance_type === "percent" && data.advance_value !== "" && Number(data.advance_value) > 100) {
        errors.advance_value = "Advance percentage cannot exceed 100.";
    }

    return errors;
};

/** Root-level vehicle fields cleared when switching away from vehicle category. */
export const VEHICLE_ROOT_FIELDS = [
    "subtitle",
    "unit_code",
    "seats",
    "current_km",
    "running_limit",
    "fuel_type",
    "transmission",
];

/**
 * @param {string | null} formType
 * @returns {{ title: string, description: string, fields: object[] } | null}
 */
export const getCategoryFieldSchema = (formType) => {
    if (!formType) return null;

    const schemas = {
        [RENTAL_FORM_TYPES.VEHICLE]: {
            title: "Vehicle details",
            description: "Specifications for cars, bikes, and other vehicles",
            icon: "fi-rr-car",
            fields: [
                { key: "subtitle", storage: "root", label: "Model / variant", placeholder: "e.g. REDI GO Petrol MT", optional: true },
                { key: "unit_code", storage: "root", label: "Unit code / plate no.", placeholder: "e.g. MH 12 AB 1234", optional: true },
                { key: "seats", storage: "root", label: "Seats", placeholder: "e.g. 5 Seater", optional: true },
                { key: "current_km", storage: "root", type: "number", label: "Current KM", placeholder: "e.g. 45000", optional: true },
                { key: "running_limit", storage: "root", label: "Running limit", placeholder: "e.g. 135 Kms / Day", optional: true },
                { key: "fuel_type", storage: "root", type: "select", options: FUEL_TYPE_OPTIONS, label: "Fuel type", placeholder: "Select fuel type", optional: false },
                { key: "transmission", storage: "root", type: "select", options: TRANSMISSION_OPTIONS, label: "Transmission", placeholder: "Select transmission", optional: false },
            ],
        },
        [RENTAL_FORM_TYPES.CLOTHING]: {
            title: "Clothing details",
            description: "Size, fit, and care information for apparel rentals",
            icon: "fi-rr-shirt",
            fields: [
                { key: "size", label: "Size", placeholder: "e.g. M, L, 32", optional: true },
                { key: "gender", type: "select", options: GENDER_OPTIONS, label: "Gender / style", placeholder: "Select", optional: true },
                { key: "color", label: "Color", placeholder: "e.g. Navy blue", optional: true },
                { key: "material", label: "Material", placeholder: "e.g. Cotton, Silk", optional: true },
                { key: "fit_type", label: "Fit type", placeholder: "e.g. Slim, Regular", optional: true },
                { key: "care_instructions", label: "Care instructions", placeholder: "e.g. Dry clean only", optional: true, colSpan: 2 },
            ],
        },
        [RENTAL_FORM_TYPES.GADGETS]: {
            title: "Gadget details",
            description: "Specs for laptops, phones, tablets, and similar devices",
            icon: "fi-rr-laptop",
            fields: [
                { key: "model", label: "Model", placeholder: "e.g. MacBook Pro 14\"", optional: true },
                { key: "storage", label: "Storage / capacity", placeholder: "e.g. 512 GB SSD", optional: true },
                { key: "condition", type: "select", options: CONDITION_OPTIONS, label: "Condition", placeholder: "Select condition", optional: true },
                { key: "battery_health", label: "Battery health", placeholder: "e.g. 92%", optional: true },
                { key: "warranty", label: "Warranty", placeholder: "e.g. Manufacturer until Dec 2026", optional: true },
                { key: "accessories_included", label: "Accessories included", placeholder: "e.g. Charger, case", optional: true, colSpan: 2 },
            ],
        },
        [RENTAL_FORM_TYPES.CAMERA]: {
            title: "Camera & gear details",
            description: "Photography and videography equipment specs",
            icon: "fi-rr-camera",
            fields: [
                { key: "model", label: "Model", placeholder: "e.g. Sony A7 IV", optional: true },
                { key: "sensor_type", label: "Sensor / format", placeholder: "e.g. Full frame", optional: true },
                { key: "lens_included", label: "Lens included", placeholder: "e.g. 24-70mm f/2.8", optional: true },
                { key: "condition", type: "select", options: CONDITION_OPTIONS, label: "Condition", placeholder: "Select condition", optional: true },
                { key: "accessories_included", label: "Accessories included", placeholder: "e.g. Tripod, memory cards", optional: true, colSpan: 2 },
            ],
        },
        [RENTAL_FORM_TYPES.PARTY]: {
            title: "Party & event equipment",
            description: "Capacity and setup details for event rentals",
            icon: "fi-rr-party-horn",
            fields: [
                { key: "capacity", label: "Capacity", placeholder: "e.g. 100 guests", optional: true },
                { key: "dimensions", label: "Dimensions", placeholder: "e.g. 10ft x 10ft", optional: true },
                { key: "setup_required", label: "Setup required", placeholder: "e.g. Yes, 2 hours", optional: true },
                { key: "power_required", label: "Power required", placeholder: "e.g. 220V outlet", optional: true },
            ],
        },
        [RENTAL_FORM_TYPES.FURNITURE]: {
            title: "Furniture details",
            description: "Dimensions and material for furniture rentals",
            icon: "fi-rr-sofa",
            fields: [
                { key: "dimensions", label: "Dimensions", placeholder: "e.g. 72\" W x 36\" D x 30\" H", optional: true },
                { key: "material", label: "Material", placeholder: "e.g. Solid wood", optional: true },
                { key: "weight_capacity", label: "Weight capacity", placeholder: "e.g. 300 lbs", optional: true },
                { key: "assembly_required", label: "Assembly required", placeholder: "e.g. Yes, tools included", optional: true },
            ],
        },
        [RENTAL_FORM_TYPES.SPORTS]: {
            title: "Sports gear details",
            description: "Equipment specs for sports and outdoor rentals",
            icon: "fi-rr-trophy",
            fields: [
                { key: "sport_type", label: "Sport / activity", placeholder: "e.g. Cycling, Skiing", optional: true },
                { key: "size", label: "Size", placeholder: "e.g. Large, 54cm frame", optional: true },
                { key: "skill_level", label: "Skill level", placeholder: "e.g. Beginner, Advanced", optional: true },
                { key: "condition", type: "select", options: CONDITION_OPTIONS, label: "Condition", placeholder: "Select condition", optional: true },
            ],
        },
        [RENTAL_FORM_TYPES.OUTDOOR]: {
            title: "Camping & trekking gear",
            description: "Specs for tents, backpacks, sleeping bags, poles, and outdoor equipment",
            icon: "fi-rr-camping",
            fields: [
                { key: "gear_type", type: "select", options: OUTDOOR_GEAR_TYPE_OPTIONS, label: "Gear type", placeholder: "Select gear type", optional: true },
                { key: "capacity", label: "Capacity / size", placeholder: "e.g. 2-person tent, 60L backpack", optional: true },
                { key: "weight", label: "Weight", placeholder: "e.g. 2.1 kg", optional: true },
                { key: "season_rating", type: "select", options: SEASON_RATING_OPTIONS, label: "Season rating", placeholder: "Select season", optional: true },
                { key: "condition", type: "select", options: CONDITION_OPTIONS, label: "Condition", placeholder: "Select condition", optional: true },
                { key: "accessories_included", label: "Accessories included", placeholder: "e.g. Rainfly, stuff sack, repair kit", optional: true, colSpan: 2 },
            ],
        },
        [RENTAL_FORM_TYPES.ELECTRONICS]: {
            title: "Electronics details",
            description: "Specs for TVs, speakers, consoles, and home electronics",
            icon: "fi-rr-plug",
            fields: [
                { key: "model", label: "Model", placeholder: "e.g. Samsung 55\" QLED", optional: true },
                { key: "power_rating", label: "Power / wattage", placeholder: "e.g. 120W", optional: true },
                { key: "condition", type: "select", options: CONDITION_OPTIONS, label: "Condition", placeholder: "Select condition", optional: true },
                { key: "accessories_included", label: "Accessories included", placeholder: "e.g. Remote, HDMI cable", optional: true, colSpan: 2 },
            ],
        },
        [RENTAL_FORM_TYPES.LUXURY]: {
            title: "Luxury item details",
            description: "Authentication and condition for high-value rentals",
            icon: "fi-rr-diamond",
            fields: [
                { key: "model", label: "Model / reference", placeholder: "e.g. Submariner 126610", optional: true },
                { key: "year", label: "Year", placeholder: "e.g. 2023", optional: true },
                { key: "serial_number", label: "Serial number", placeholder: "Optional", optional: true },
                { key: "authentication_certificate", label: "Authentication / certificate", placeholder: "e.g. Included with rental", optional: true, colSpan: 2 },
                { key: "condition", type: "select", options: CONDITION_OPTIONS, label: "Condition", placeholder: "Select condition", optional: true },
            ],
        },
    };

    const schema = schemas[formType];
    if (!schema) return null;

    return {
        ...schema,
        fields: schema.fields.map((f) => ({
            storage: "category_attributes",
            type: "text",
            optional: true,
            colSpan: 1,
            ...f,
        })),
    };
};

/**
 * Reset fields that do not apply to the newly selected category form type.
 */
export const buildCategoryChangePatch = (prev, newFormType) => {
    const patch = { category_attributes: {}, feature_ids: [] };

    if (newFormType !== RENTAL_FORM_TYPES.VEHICLE) {
        VEHICLE_ROOT_FIELDS.forEach((key) => {
            patch[key] = "";
        });
        if (prev?.quantity === "" || prev?.quantity == null) {
            patch.quantity = 1;
        }
    } else {
        patch.quantity = 1;
    }

    return patch;
};

export const parseCategoryAttributes = (value) => {
    if (!value) return {};
    if (typeof value === "object" && !Array.isArray(value)) return value;
    if (typeof value === "string") {
        try {
            const parsed = JSON.parse(value);
            return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
        } catch {
            return {};
        }
    }
    return {};
};

export const getTitlePlaceholder = (formType) => {
    switch (formType) {
        case RENTAL_FORM_TYPES.VEHICLE:
            return "e.g. E Hatchback";
        case RENTAL_FORM_TYPES.CLOTHING:
            return "e.g. Designer Sherwani – Size 40";
        case RENTAL_FORM_TYPES.GADGETS:
            return "e.g. MacBook Pro 14\" M3";
        case RENTAL_FORM_TYPES.CAMERA:
            return "e.g. Sony A7 IV Kit";
        case RENTAL_FORM_TYPES.OUTDOOR:
            return "e.g. 2-Person Dome Tent";
        default:
            return "e.g. Premium rental item";
    }
};
